import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state'

import { Badge, Button, Divider, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'

import {
  useGetTimeTrackingConsentQuery,
  useGetCurrentTimeTrackingSessionQuery,
  useStartTimeTrackingSessionMutation,
  useForceStartTimeTrackingSessionMutation,
  useStopTimeTrackingSessionMutation,
} from '@/core/api'
import { TimeSession } from '@/core/api/time-tracking'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'
import { getDeviceId } from '../lib/deviceId'
import { ConflictSessionDialog } from './ConflictSessionDialog'

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

export function TimeTrackingNotifications() {
  useLingui()
  const navigate = useNavigate()

  const authUser = useAppSelector(selectAuthUser)
  const isStaffMember = isStaff(authUser)

  const { data: consent } = useGetTimeTrackingConsentQuery(undefined, { skip: !isStaffMember })
  const skip = !isStaffMember || !consent?.enabled

  const { data: currentSession, refetch } = useGetCurrentTimeTrackingSessionQuery(undefined, {
    skip,
    pollingInterval: 5 * 60 * 1000,
  })

  const [startSession, { isLoading: isStarting }] = useStartTimeTrackingSessionMutation()
  const [forceStartSession, { isLoading: isForceStarting }] = useForceStartTimeTrackingSessionMutation()
  const [stopSession, { isLoading: isStopping }] = useStopTimeTrackingSessionMutation()

  const [conflictSession, setConflictSession] = useState<TimeSession | null>(null)

  const isSessionActive = !!currentSession?.isOpen

  const [elapsed, setElapsed] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (!currentSession?.startTime || !currentSession.isOpen) {
      setElapsed(0)
      return
    }

    const startTime = new Date(currentSession.startTime).getTime()
    const tick = () => setElapsed(Math.floor((Date.now() - startTime) / 1000))

    tick()
    intervalRef.current = setInterval(tick, 1000)
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [currentSession?.startTime, currentSession?.isOpen])

  const handleStart = async () => {
    try {
      const result = await startSession({ deviceId: getDeviceId() }).unwrap()
      if (result.conflict) {
        setConflictSession(result.existingSession)
      } else {
        refetch()
      }
    } catch {}
  }

  const handleForceStart = useCallback(async () => {
    try {
      await forceStartSession({ deviceId: getDeviceId() }).unwrap()
      setConflictSession(null)
      refetch()
    } catch {}
  }, [forceStartSession, refetch])

  const handleStop = async () => {
    try {
      await stopSession().unwrap()
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setElapsed(0)
      refetch()
    } catch {}
  }

  if (skip) return null

  return (
    <>
      <PopupState variant="popover" popupId="time-tracking-session-control">
        {(popupState) => (
          <>
            <Tooltip title={t`Time Tracking`}>
              <IconButton
                {...bindTrigger(popupState)}
                sx={{
                  width: 40,
                  height: 40,
                  background: 'transparent',
                  border: (theme) => `1px solid ${theme.palette.primary.light}`,
                  color: (theme) => theme.palette.background.default,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.light,
                  },
                }}
              >
                <Badge
                  color={isSessionActive ? 'success' : 'default'}
                  overlap="circular"
                  variant="dot"
                  sx={{ position: 'unset' }}
                >
                  <AccessTimeRoundedIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Popover
              {...bindPopover(popupState)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Stack width={300} p={2} spacing={2}>
                <Typography variant="h6" align="center">
                  <Trans>Time Tracking</Trans>
                </Typography>
                <Divider />

                {/* Session clock */}
                <Stack alignItems="center" spacing={1}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 700,
                      color: isSessionActive ? 'text.primary' : 'text.disabled',
                    }}
                  >
                    {formatElapsed(elapsed)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isSessionActive ? (
                      <Trans>Session in progress</Trans>
                    ) : (
                      <Trans>No active session</Trans>
                    )}
                  </Typography>
                </Stack>

                <Divider />

                {/* Action buttons */}
                <Stack direction="row" spacing={1} justifyContent="center">
                  {isSessionActive ? (
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<StopRoundedIcon />}
                      onClick={handleStop}
                      disabled={isStopping}
                      fullWidth
                    >
                      <Trans>Stop</Trans>
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PlayArrowRoundedIcon />}
                      onClick={handleStart}
                      disabled={isStarting}
                      fullWidth
                    >
                      <Trans>Start</Trans>
                    </Button>
                  )}
                </Stack>

                {/* Link to full page */}
                <Button
                  size="small"
                  endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    popupState.close()
                    navigate('/time-tracking')
                  }}
                  sx={{ alignSelf: 'center' }}
                >
                  <Trans>View details</Trans>
                </Button>
              </Stack>
            </Popover>
          </>
        )}
      </PopupState>
      <ConflictSessionDialog
        session={conflictSession}
        loading={isForceStarting}
        onContinue={handleForceStart}
        onCancel={() => setConflictSession(null)}
      />
    </>
  )
}
